import React from "react";
import { useTranslation } from "react-i18next";
import { FaCopy } from "react-icons/fa";
import { HiCheck, HiExclamationCircle } from "react-icons/hi";
import { twMerge } from "tailwind-merge";
import { useCopyToClipboard } from "./useCopyToClipboard";

interface CopyProps {
  target: string;
  truncate?: boolean;
  longVersion?: boolean;
  iconOnly?: boolean;
  title?: string;
  className?: string;
  iconClassName?: string;
}

export const Copy: React.FC<CopyProps> = ({
  target,
  truncate,
  longVersion,
  iconOnly,
  title,
  className,
  iconClassName,
}) => {
  const { t } = useTranslation();
  const { status, copy } = useCopyToClipboard();

  const feedbackTitle =
    status === "success"
      ? (t("common.copied") as string)
      : status === "error"
      ? (t("common.copyFailed") as string)
      : title;

  const icon =
    status === "success" ? (
      <HiCheck className={twMerge("text-green-600", iconClassName)} />
    ) : status === "error" ? (
      <HiExclamationCircle className={twMerge("text-red-600", iconClassName)} />
    ) : (
      <FaCopy className={twMerge("text-gray-400", iconClassName)} />
    );

  const displayText = (() => {
    if (!truncate) return target;

    if (longVersion) {
      if (target.length > 33) {
        return `${target.substring(0, 15)}...${target.substring(
          target.length - 15
        )}`;
      }
    } else {
      if (target.length > 14) {
        return `${target.substring(0, 5)}...${target.substring(
          target.length - 5
        )}`;
      }
    }

    return target;
  })();

  if (iconOnly) {
    return (
      <button
        type="button"
        onClick={() => copy(target)}
        className={twMerge("inline-flex shrink-0", className)}
        translate="no"
        title={feedbackTitle}
      >
        {icon}
      </button>
    );
  }

  return (
    <div
      translate="no"
      className={twMerge("flex items-center min-w-0 max-w-full", className)}
    >
      <span className="truncate min-w-0" title={target}>
        {displayText}
      </span>
      <button
        type="button"
        onClick={() => copy(target)}
        className="inline-flex ml-2 shrink-0"
        translate="no"
        title={feedbackTitle}
      >
        {icon}
      </button>
    </div>
  );
};
